import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const Home = () => {
    // State for the typing animation
    const [h1Text, setH1Text] = useState('');
    const [pText, setPText] = useState('');
    const [currentH1Index, setCurrentH1Index] = useState(0);
    const [isDeleting, setIsDeleting] = useState(false);
    const [isTyping, setIsTyping] = useState(true);
    const [isAutoPlaying, setIsAutoPlaying] = useState(true);
    const [isManualSwitch, setIsManualSwitch] = useState(false);

    const typingSpeed = useRef(150);
    const deletingSpeed = useRef(75);
    const pauseDuration = 10000;

    const h1Messages = [
        "Digital Innovation, Crafted in the Heart of Nairobi",
        "Transform Your Business with Mobile Apps",
        "Building Tomorrow's Solutions, Today",
        "Your Vision, Our Code, One Solution"
    ];

    const pMessages = [
        "We build powerful web applications, stunning websites, and intuitive mobile apps that drive growth and solve real-world challenges for businesses across Kenya and beyond.",
        "Tap into Kenya's mobile revolution with custom iOS and Android apps designed for user engagement and business growth. Reach millions of smartphone users with intuitive mobile experiences.",
        "Our team of experts leverages cutting-edge technology to deliver scalable and robust software solutions tailored to your business needs.",
        "Partner with us to transform your digital presence and accelerate your business growth in today's competitive market."
    ];

    const timerRef = useRef(null);

    const clearTimer = () => {
        if (timerRef.current) {
            clearTimeout(timerRef.current);
            timerRef.current = null;
        }
    };

    useEffect(() => {
        clearTimer();

        if (!isTyping && !isAutoPlaying) {
            return;
        }

        const currentMessage = h1Messages[currentH1Index];

        const typeNextChar = () => {
            if (!isDeleting && h1Text === currentMessage) {
                timerRef.current = setTimeout(() => {
                    if (isAutoPlaying && !isManualSwitch) {
                        setIsDeleting(true);
                    }
                }, pauseDuration);
            } else if (isDeleting && h1Text === '') {
                setIsDeleting(false);
                setIsManualSwitch(false);

                const nextIndex = (currentH1Index + 1) % h1Messages.length;
                setCurrentH1Index(nextIndex);
                setPText(pMessages[nextIndex]);

                timerRef.current = setTimeout(() => {
                    setIsTyping(true);
                }, 500);
            } else if (isTyping || isDeleting) {
                const speed = isDeleting ? deletingSpeed.current : typingSpeed.current;
                timerRef.current = setTimeout(() => {
                    if (isDeleting) {
                        setH1Text(currentMessage.substring(0, h1Text.length - 1));
                    } else {
                        setH1Text(currentMessage.substring(0, h1Text.length + 1));
                    }
                }, speed);
            }
        };

        typeNextChar();

        return () => clearTimer();
    }, [h1Text, isTyping, isDeleting, currentH1Index, isAutoPlaying, isManualSwitch]);

    useEffect(() => {
        setPText(pMessages[0]);
    }, []);

    const handleBulletClick = (index) => {
        if (index === currentH1Index) return;

        clearTimer();
        setIsManualSwitch(true);
        setIsAutoPlaying(false);
        setIsDeleting(false);
        setIsTyping(false);
        setCurrentH1Index(index);
        setH1Text(h1Messages[index]);
        setPText(pMessages[index]);

        timerRef.current = setTimeout(() => {
            setIsAutoPlaying(true);
            setIsManualSwitch(false);
            setIsTyping(true);
        }, 3000);
    };

    const handleAutoPlayToggle = () => {
        if (isAutoPlaying) {
            setIsAutoPlaying(false);
            clearTimer();
        } else {
            setIsAutoPlaying(true);
            setIsTyping(true);
        }
    };

    return (
        <>
            {/* Hero Section */}
            <section className="hero home-hero" id="home">
                <div className="container">
                    <h1 className="typing-animation-title">
                        {h1Text}
                        {isTyping && !isDeleting && (
                            <span className="typing-cursor"></span>
                        )}
                    </h1>
                    <p>{pText}</p>

                    <div className="hero-btns">
                        <Link to="/services" className="btn">View Our Services</Link>
                        <Link to="/portfolio#mobile" className="btn btn-secondary">See Our Mobile App Projects</Link>
                    </div>

                    <div className="carousel-bullets">
                        {h1Messages.map((_, index) => (
                            <div
                                key={index}
                                className={`carousel-bullet ${index === currentH1Index ? 'active' : ''} ${index === currentH1Index && !isAutoPlaying ? 'paused' : ''}`}
                                onClick={() => handleBulletClick(index)}
                                title={`View message ${index + 1}`}
                            />
                        ))}

                        <button
                            className="autoplay-toggle"
                            onClick={handleAutoPlayToggle}
                            title={isAutoPlaying ? "Pause auto-play" : "Resume auto-play"}
                        >
                            {isAutoPlaying ? '⏸️' : '▶️'}
                        </button>
                    </div>
                </div>
            </section>

            {/* Services Section */}
            <section id="services" className="services-section">
                <div className="container">
                    <div className="section-title">
                        <h2>Our Services</h2>
                        <p>Comprehensive digital solutions with a strong focus on mobile app development</p>
                    </div>
                    <div className="services-grid">
                        <div className="service-card mobile-highlight">
                            <div className="service-icon">
                                <i className="fas fa-mobile-alt"></i>
                            </div>
                            <h3>Mobile Applications</h3>
                            <p>Engaging iOS and Android apps that connect you with millions of smartphone users. We build mobile solutions that drive user engagement and business growth across Kenya.</p>
                            <ul>
                                <li><strong>iOS Apps</strong> - Reach Apple device users</li>
                                <li><strong>Android Apps</strong> - Connect with the widest smartphone market</li>
                                <li><strong>Offline and Online Functionality</strong> - Suitable for remote areas with limited internet access</li>
                                <li><strong>Push Notifications</strong> for user engagement</li>
                                <li><strong>Integration with M-Pesa</strong></li>
                            </ul>
                            <Link to="/services" className="btn mobile-services-btn">Explore Mobile Services</Link>
                        </div>

                        <div className="service-card">
                            <div className="service-icon">
                                <i className="fas fa-laptop-code"></i>
                            </div>
                            <h3>Web Applications</h3>
                            <p>Scalable, secure, and dynamic web apps that streamline your business operations and empower your team.</p>
                            <ul>
                                <li>School & Hospital Management Systems</li>
                                <li>SACCO Management Systems</li>
                                <li>Church Membership and Management Systems</li>
                                <li><strong>Bulk SMS</strong> and <strong>MPesa Payment Integration</strong></li>
                                <li>Fleet Management Systems</li>
                                <li>Supermarket and POS Management Systems</li>
                                <li>Milk Collection Systems</li>
                            </ul>
                        </div>

                        <div className="service-card">
                            <div className="service-icon">
                                <i className="fas fa-globe"></i>
                            </div>
                            <h3>Websites</h3>
                            <p>Beautiful, responsive, and fast-loading websites that make a powerful first impression and convert visitors into customers.</p>
                            <ul>
                                <li>Corporate Websites</li>
                                <li>E-commerce Sites</li>
                                <li>Portfolio & Business Sites</li>
                                <li>Church & NGO Websites</li>
                                <li>Hotel & Travel Websites</li>
                                <li>Content Management Systems (Wordpress)</li>
                                <li>Vlog & Blog Sites</li>
                            </ul>
                        </div>
                    </div>
                </div>
            </section>

            {/* Portfolio Section */}
            <section className="portfolio" id="portfolio">
                <div className="container">
                    <div className="section-title">
                        <h2>Our Work</h2>
                        <p>See how we've helped businesses succeed with our digital solutions</p>
                    </div>
                    <div className="portfolio-grid">
                        <div className="portfolio-card mobile-app">
                            <Link to="/project/family-media">

                                <div className="portfolio-img portfolio-img-familytv">
                                    <div className="app-badge">📱 Mobile App</div>
                                </div>
                            </Link>
                            <div className="portfolio-content">
                                <Link to="/project/family-media" className='no_deco_link'><h3>Family Media App</h3></Link>
                                <p>A comprehensive media platform for the Kenya's leading christian media station, TBN Family Media. It allows users to watch Family TV, listen to Family Radio and Radio 316 live on their mobile devices.</p>
                                <p><strong>Features:</strong> Live TV streaming, radio streaming, on-demand content, cross-platform compatibility</p>
                                <div className="app-store-badges">
                                    <a href="https://play.google.com/store/apps/details?id=com.familymedia.tv" target="_blank" rel="noopener noreferrer" className="store-link">
                                        <img src="/images/googleplay.jpg" alt="Download on Google Play" className="store-badge" />
                                    </a>
                                    <a href="https://apps.apple.com/us/app/family-media/id1488345441" target="_blank" rel="noopener noreferrer" className="store-link">
                                        <img src="/images/appstore.jpg" alt="Download on the App Store" className="store-badge" />
                                    </a>
                                </div>
                                <Link to="/project/family-media" className="btn btn-secondary portfolio-btn">View Project Details</Link>
                            </div>
                        </div>

                        <div className="portfolio-card mobile-app">
                            <Link to="/project/milk-collection-app-for-dairy-management">
                                <div className="portfolio-img portfolio-img-milkstars">
                                    <div className="app-badge">📱 Mobile App</div>
                                   
                                </div>
                            </Link>
                            <div className="portfolio-content">
                                <Link to="/project/milk-collection-app-for-dairy-management" className="no_deco_link"><h3>Milk Collection App</h3></Link>
                                <p>Revolutionary dairy management system that uses Bluetooth technology to streamline milk collection and payment processes.</p>
                                <p><strong>Features:</strong> Bluetooth scale integration, receipt printing, farmer database, payment tracking</p>
                                <Link to="/project/milk-collection-app-for-dairy-management" className="btn btn-secondary portfolio-btn">View Project Details</Link>
                            </div>
                        </div>

                        <div className="portfolio-card">
                            <div className="portfolio-img portfolio-img-sokoni">
                                Sokoni Connect
                            </div>
                            <div className="portfolio-content">
                                <h3>Sokoni Connect</h3>
                                <p>An e-commerce platform connecting local artisans with global customers, featuring integrated M-Pesa payments.</p>
                                <p><strong>Results:</strong> 200% increase in vendor sales within 6 months</p>
                                <Link to="/project/sokoni-connect" className="btn btn-secondary portfolio-btn">View Project Details</Link>
                            </div>
                        </div>
                    </div>
                    <div className="portfolio-all-btn">
                        <Link to="/portfolio" className="btn">View All Projects</Link>
                    </div>
                </div>
            </section>

            {/* About Section */}
            <section id="about" className="about-section">
                <div className="container">
                    <div className="about-content">
                        <div className="about-text">
                            <h2>Driving Kenya's Digital Future</h2>
                            <p>Founded in Nairobi, Kenya, Zanify Technologies was born from a passion for technology and a belief in the immense potential of the Kenyans.</p>
                            <p>Our name, "Zanify," embodies our mission: to amplify and empower businesses through the power of technology.</p>
                            <p>We are a diverse collective of talented developers, creative designers, and strategic project managers, all united by a shared drive to build digital solutions that matter.</p>
                            <Link to="/contact" className="btn">Get In Touch</Link>
                        </div>
                        <div className="about-image">
                            <img src="/images/nairobiteam.png" alt="Our Team in Nairobi" />
                        </div>
                    </div>
                    <div className="values-grid">
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fas fa-lightbulb"></i>
                            </div>
                            <h3>Innovation</h3>
                            <p>We constantly explore new technologies and creative approaches to solve complex problems.</p>
                        </div>
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fas fa-handshake"></i>
                            </div>
                            <h3>Collaboration</h3>
                            <p>We see ourselves as an extension of your team, working together to achieve your goals.</p>
                        </div>
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fas fa-award"></i>
                            </div>
                            <h3>Excellence</h3>
                            <p>We are committed to delivering superior quality in every project we undertake.</p>
                        </div>
                        <div className="value-card">
                            <div className="value-icon">
                                <i className="fas fa-shield-alt"></i>
                            </div>
                            <h3>Integrity</h3>
                            <p>We build relationships on trust, transparency, and honesty with all our clients.</p>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default Home;